import React, { useState, useEffect, useRef } from 'react';
import { Contact } from '../types/Contact';
import { User } from '@supabase/supabase-js';
import {
    BookOpen,
    Edit3,
    X,
    Archive,
    ArchiveRestore,
    Share2,
    Bot,
    Users,
    TrendingUp,
    Check,
    Settings,
    Search,
    UploadCloud,
    Mic
} from 'lucide-react';
import { Plus } from 'lucide-react';
import { useContactData } from '../hooks/useContactData';
import { useModalGestures } from '../hooks/useModalGestures';
import { useNotifications } from '../hooks/useNotifications';
import { useContactArchiving } from '../hooks/useContactArchiving';
import { supabase } from '../lib/supabase';
import { updateContactCore } from '../lib/contactsCore';
import ContactHeader from './ContactHeader';
import ContactInfo from './ContactInfo';
import StatusTierManager from './StatusTierManager';
import TranscribeSaveEventModal from './TranscribeSaveEventModal';
import { uploadAudioToStorage, firefliesUploadFromStorage, waitForTranscriptId, firefliesGetStatus, type DiarizedSegment } from '../lib/transcribe';
import { createEvent } from '../lib/events';
import ShareModal from './ShareModal';
import ExpandedImageModal from './ExpandedImageModal';
import NotificationToast from './NotificationToast';
import PersonalizedMessageGenerator from './PersonalizedMessageGenerator';
import TLDRGenerator from './TLDRGenerator';
import SquareLoader from './SquareLoader';

interface ContactViewModalProps {
    contact: Contact;
    onClose: () => void;
    onSave: () => void;
    user?: User | null;
    personalProfile?: string;
    collectionContext?: string;
    collectionName?: string;
    collectionId?: string;
}

export default function ContactViewModal({ contact, onClose, onSave, user, personalProfile, collectionContext, collectionName, collectionId }: ContactViewModalProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isImageExpanded, setIsImageExpanded] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [originalFormData, setOriginalFormData] = useState<any>(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showMessageGenerator, setShowMessageGenerator] = useState(false);
    const [activeTab, setActiveTab] = useState<'details' | 'ai' | 'options'>(collectionContext ? 'ai' : 'details');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResult, setSearchResult] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [showGuessOption, setShowGuessOption] = useState(false);
    const [lastSearchQuery, setLastSearchQuery] = useState('');
    const [isGuessingMode, setIsGuessingMode] = useState(false);
    const [showSmartNoteInput, setShowSmartNoteInput] = useState(false);
    const [smartNoteContent, setSmartNoteContent] = useState('');
    const [isParsingNote, setIsParsingNote] = useState(false);
    const [noteHistory, setNoteHistory] = useState<Array<{ id: string, content: string, date: string }>>([]);
    const [showNoteHistory, setShowNoteHistory] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    // Transcribe flow state
    const [audioFile, setAudioFile] = useState<File | null>(null);
    const [transBusy, setTransBusy] = useState(false);
    const [transError, setTransError] = useState<string | null>(null);
    const [transSegments, setTransSegments] = useState<DiarizedSegment[]>([]);
    const [transText, setTransText] = useState('');
    const [showTranscribeModal, setShowTranscribeModal] = useState(false);

    const { formData, setFormData, loading, handleInputChange, handleSave } = useContactData(contact, user);
    const { notification, showNotification, hideNotification } = useNotifications();
    const { isArchiving, handleArchiveToggle } = useContactArchiving({
        contact,
        onNotification: showNotification,
        onSave,
        onClose
    });
    const {
        sheetRef,
        handleDragStart,
        handleDragMove,
        handleDragEnd,
        handleBackdropClick,
        handleClose
    } = useModalGestures({ onClose, isVisible });

    // Store original data when entering edit mode
    useEffect(() => {
        if (isEditing && !originalFormData) {
            setOriginalFormData({ ...formData });
        }
    }, [isEditing, formData, originalFormData]);

    // Track changes to show save button only when needed
    const handleInputChangeWithTracking = (field: string, value: any) => {
        handleInputChange(field, value);
        setHasChanges(true);
    };

    // Reset changes tracking when switching edit mode
    const toggleEditMode = () => {
        setIsEditing(!isEditing);
        if (!isEditing) {
            setHasChanges(false);
            setOriginalFormData({ ...formData });
        }
    };

    // Discard changes and exit edit mode
    const handleDiscard = () => {
        if (originalFormData) {
            // Restore original data
            Object.keys(originalFormData).forEach(key => {
                handleInputChange(key, originalFormData[key]);
            });
        }
        setHasChanges(false);
        setIsEditing(false);
        setOriginalFormData(null);
    };

    // Parse Google contact data
    // Show modal with animation
    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 10);
        // Scroll to top when this modal opens
        try { window.scrollTo({ top: 0, behavior: 'instant' as any }); } catch { window.scrollTo(0, 0); }
        return () => clearTimeout(timer);
    }, []);

    // Also re-scroll to top when switching to a different contact in this modal
    useEffect(() => {
        try { window.scrollTo({ top: 0, behavior: 'instant' as any }); } catch { window.scrollTo(0, 0); }
    }, [contact?.id]);

    const onSaveWithCallback = () => {
        handleSave(onSave);
        setHasChanges(false);
        setIsEditing(false);
        setOriginalFormData(null);
    };

    // Handle sharing contact
    const handleShareContact = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowShareModal(true);
    };

    // Start upload + transcribe for selected audio file
    const handleStartTranscribe = async () => {
        if (!audioFile) return;
        setTransBusy(true);
        setTransError(null);
        try {
            const { path } = await uploadAudioToStorage(audioFile, 'transcripts');
            const title = audioFile.name.replace(/\.[^.]+$/, '') || 'Transcript';
            const attendees = [{ name: `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || 'Contact' }];
            const res: any = await firefliesUploadFromStorage({ storagePath: path, title, attendees, userId: user?.id || null });
            const transcriptId = await waitForTranscriptId(res.client_reference_id);
            // Poll quick status once; if not done, we still open modal with any partial text after a couple of checks
            let tries = 0;
            while (tries < 30) { // ~2 minutes max
                const st = await firefliesGetStatus(transcriptId);
                if (/completed|done|ready/i.test(st.status) || (st.segments && st.segments.length) || (st.text && st.text.length)) {
                    setTransSegments(st.segments || []);
                    setTransText(st.text || '');
                    break;
                }
                await new Promise(r => setTimeout(r, 4000));
                tries++;
            }
            setShowTranscribeModal(true);
        } catch (e: any) {
            setTransError(e?.message || 'Failed to transcribe audio');
        } finally {
            setTransBusy(false);
        }
    };

    // Handle status toggle
    const handleToggleContactStatus = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const currentStatus = formData.status || 'Not Contacted';
            const newStatus = currentStatus === 'Called' ? 'Not Contacted' : 'Called';
            const lastContactedTime = newStatus === 'Called' ? new Date().toISOString() : null;

            // Update in database via core API
            await updateContactCore(supabase as any, String(contact.id), {
                status: newStatus as any,
                last_contacted: lastContactedTime as any,
            });

            // Update local state
            setFormData(prev => ({
                ...prev,
                status: newStatus,
                last_contacted: lastContactedTime
            }));

            // Update original data to avoid conflicts
            if (originalFormData) {
                setOriginalFormData(prev => ({
                    ...prev!,
                    status: newStatus,
                    last_contacted: lastContactedTime
                }));
            }

            // Trigger callback to refresh parent data
            if (onSave) {
                onSave();
            }
        } catch (error) {
            console.error('Error updating contact status:', error);
            showNotification('Failed to update contact status. Please try again.', 'error');
        }
    };

    // Handle natural search through contact data
    const handleNaturalSearch = async (makeEducatedGuess = false) => {
        if (!searchQuery.trim() || !user) return;

        setIsSearching(true);
        if (!makeEducatedGuess) {
            setSearchResult('');
            setShowGuessOption(false);
            setIsGuessingMode(false);
        }

        try {
            // Compile all contact data into searchable text
            const contactData = {
                name: `${formData.first_name || ''} ${formData.last_name || ''}`.trim(),
                company: formData.company || '',
                title: formData.title || '',
                email: formData.email || '',
                phone: formData.phone || '',
                location: formData.location || '',
                personal_details: formData.personal_details || '',
                investment_reasoning: formData.personal_investment_reasoning || '',
                referral_reasoning: formData.referral_potential_reasoning || '',
                approach: formData.recommended_approach || '',
                key_factors: formData.key_factors || '',
                summary: formData.summary || '',
                investment_capacity: formData.estimated_investment_capacity || '',
                financial_sophistication: formData.financial_sophistication || '',
                accredited_likelihood: formData.accredited_investor_likelihood || '',
                investment_score: formData.personal_investment_score || '',
                referral_score: formData.referral_potential_score || '',
                status: formData.status || '',
                tier: formData.tier || '',
                last_contacted: formData.last_contacted || '',
                notes: formData.notes || ''
            };

            // Create different prompts based on whether we're making an educated guess
            let searchPrompt;

            if (makeEducatedGuess) {
                searchPrompt = `You are an AI assistant making educated guesses about contact information based on available data.

Contact Information:
Name: ${contactData.name}
Company: ${contactData.company}
Title: ${contactData.title}
Email: ${contactData.email}
Phone: ${contactData.phone}
Location: ${contactData.location}
Status: ${contactData.status}
Tier: ${contactData.tier}
Last Contacted: ${contactData.last_contacted}

Investment Information:
Investment Score: ${contactData.investment_score}/10
Referral Score: ${contactData.referral_score}/10
Investment Capacity: ${contactData.investment_capacity}
Financial Sophistication: ${contactData.financial_sophistication}
Accredited Likelihood: ${contactData.accredited_likelihood}

Personal Details:
${contactData.personal_details}

Investment Reasoning:
${contactData.investment_reasoning}

Referral Reasoning:
${contactData.referral_reasoning}

Recommended Approach:
${contactData.approach}

Key Factors:
${contactData.key_factors}

Summary:
${contactData.summary}

Notes:
${contactData.notes}

User Question: "${searchQuery.trim()}"

IMPORTANT: The user has asked for an educated guess since we don't have direct information. Please:
1. Make an educated inference based on the available data
2. Clearly state this is an educated guess
3. Provide a confidence percentage (1-100%) for how reliable you think this guess is
4. Explain your reasoning for the guess
5. Format your response like: "EDUCATED GUESS (X% confidence): [your guess] 

REASONING: [explain why you think this based on the available data]"

Be thoughtful and conservative with confidence percentages - only use high percentages when you have strong supporting evidence.`;
            } else {
                searchPrompt = `You are an AI assistant helping to search through contact information. 

Contact Information:
Name: ${contactData.name}
Company: ${contactData.company}
Title: ${contactData.title}
Email: ${contactData.email}
Phone: ${contactData.phone}
Location: ${contactData.location}
Status: ${contactData.status}
Tier: ${contactData.tier}
Last Contacted: ${contactData.last_contacted}

Investment Information:
Investment Score: ${contactData.investment_score}/10
Referral Score: ${contactData.referral_score}/10
Investment Capacity: ${contactData.investment_capacity}
Financial Sophistication: ${contactData.financial_sophistication}
Accredited Likelihood: ${contactData.accredited_likelihood}

Personal Details:
${contactData.personal_details}

Investment Reasoning:
${contactData.investment_reasoning}

Referral Reasoning:
${contactData.referral_reasoning}

Recommended Approach:
${contactData.approach}

Key Factors:
${contactData.key_factors}

Summary:
${contactData.summary}

Notes:
${contactData.notes}

User Question: "${searchQuery.trim()}"

IMPORTANT: If you cannot find specific information to answer the user's question in the provided data, respond with:
"I don't have specific information about [the topic] in ${contactData.name}'s contact data. Would you like me to make an educated guess based on the available information?"

If you CAN answer the question with the provided data, give a specific, helpful answer.`;
            }

            const response = await supabase.functions.invoke('gemini-ai', {
                body: {
                    prompt: searchPrompt
                }
            });

            if (response.error) {
                throw new Error(response.error.message || 'Failed to search contact data');
            }

            const result = response.data?.response || 'No relevant information found for your query.';

            if (!makeEducatedGuess && result.toLowerCase().includes("would you like me to make an educated guess")) {
                setShowGuessOption(true);
                setLastSearchQuery(searchQuery);
            } else {
                setShowGuessOption(false);
                if (makeEducatedGuess) {
                    setIsGuessingMode(true);
                }
            }

            setSearchResult(result);

        } catch (error) {
            console.error('Error searching contact data:', error);
            setSearchResult('Sorry, there was an error searching the contact data. Please try again.');
            showNotification('Error searching contact data', 'error');
        } finally {
            setIsSearching(false);
        }
    };

    // Handle educated guess request
    const handleEducatedGuess = () => {
        setSearchQuery(lastSearchQuery);
        handleNaturalSearch(true);
    };

    // Handle declining the guess
    const handleDeclineGuess = () => {
        setShowGuessOption(false);
        setSearchResult('');
        setSearchQuery('');
    };

    // Handle smart note parsing
    const handleSmartNoteParsing = async () => {
        if (!smartNoteContent.trim() || !user) return;

        setIsParsingNote(true);

        try {
            // Create a prompt for parsing the note into structured fields
            const parsePrompt = `You are an AI assistant that parses unstructured notes about a contact into specific fields. 

Current Contact Information:
Name: ${formData.first_name} ${formData.last_name}
Company: ${formData.company || 'Unknown'}
Title: ${formData.title || 'Unknown'}

Note to Parse:
"${smartNoteContent.trim()}"

Please extract information from this note and categorize it into the following fields. Return a JSON object with ONLY the fields that have relevant information from the note. Do not make up information or return empty/null values.

Available Fields:
- linkedin_description: Professional description or LinkedIn bio information
- professional_experience: Work history, career details, job responsibilities
- education_background: Education, degrees, schools, certifications
- skills_expertise: Technical skills, expertise areas, specializations
- achievements: Awards, accomplishments, recognitions
- personal_interests: Hobbies, interests, personal activities
- location_info: Location details, addresses, geographical information
- company_details: Information about their company, company culture, business details
- contact_preferences: Communication preferences, best contact methods/times
- tldr_summary: Brief summary of the person
- about_section: General background information
- comprehensive_notes: Meeting notes, observations, detailed information
- personal_details: Personal life, family, relationships
- personal_investment_reasoning: Investment-related thoughts or capacity indicators
- referral_potential_reasoning: Network or referral potential insights
- recommended_approach: How to best approach or work with this person
- key_factors: Important factors to remember about this person

Return ONLY a valid JSON object with extracted fields. Example:
{
  "professional_experience": "Works as Senior Developer at TechCorp for 5 years",
  "personal_interests": "Enjoys hiking and photography",
  "education_background": "MBA from Stanford University"
}`;

            const response = await supabase.functions.invoke('gemini-ai', {
                body: {
                    prompt: parsePrompt
                }
            });

            if (response.error) {
                throw new Error(response.error.message || 'Failed to parse note');
            }

            const result = response.data?.response || '';

            try {
                // Try to extract JSON from the response
                const jsonMatch = result.match(/\{[\s\S]*\}/);
                if (!jsonMatch) {
                    throw new Error('No JSON found in response');
                }

                const parsedData = JSON.parse(jsonMatch[0]);

                // Update the contact with parsed data
                Object.keys(parsedData).forEach(key => {
                    if (parsedData[key] && parsedData[key].trim()) {
                        handleInputChangeWithTracking(key as keyof Contact, parsedData[key]);
                    }
                });

                // Save the raw note to history
                const noteId = Date.now().toString();
                const newNote = {
                    id: noteId,
                    content: smartNoteContent.trim(),
                    date: new Date().toISOString()
                };

                setNoteHistory(prev => [newNote, ...prev]);

                // Clear the input
                setSmartNoteContent('');
                setShowSmartNoteInput(false);

                showNotification(`Successfully parsed note and updated ${Object.keys(parsedData).length} fields`, 'success');

            } catch (parseError) {
                console.error('Error parsing AI response:', parseError);
                // Fallback: add to comprehensive notes
                const currentNotes = formData.comprehensive_notes || '';
                const timestamp = new Date().toLocaleString();
                const newNote = `[${timestamp}] ${smartNoteContent.trim()}`;
                const updatedNotes = currentNotes ? `${currentNotes}\n\n${newNote}` : newNote;

                handleInputChangeWithTracking('comprehensive_notes', updatedNotes);

                setSmartNoteContent('');
                setShowSmartNoteInput(false);

                showNotification('Note added to comprehensive notes (parsing failed)', 'warning');
            }

        } catch (error) {
            console.error('Error parsing smart note:', error);
            showNotification('Error parsing note', 'error');
        } finally {
            setIsParsingNote(false);
        }
    };

    return (
        <>
            {/* Backdrop - Simplified for better click detection */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[10000]"
                onClick={handleBackdropClick}
                style={{
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 300ms ease-in-out'
                }}
            />

            {/* Bottom Sheet - Improved luxury styling */}
            <div
                ref={sheetRef}
                className={`fixed inset-x-0 bottom-0 bg-gradient-to-b from-gray-800 to-gray-900 rounded-t-2xl shadow-2xl z-[10001] transform transition-transform duration-300 cubic-bezier(0.32, 0.72, 0, 1) border-t border-gray-600 flex flex-col ${isVisible ? 'translate-y-0' : 'translate-y-full'
                    }`}
                style={{ height: '95vh' }}
            >
                {/* Fixed Header with Edit Button and Drag Handle */}
                <div className="flex-shrink-0 w-full relative px-6 py-4 bg-gradient-to-b from-gray-700 to-gray-800 border-b border-gray-600">
                    {/* Drag Handle and Share Button Container */}
                    <div className="flex items-center justify-between">
                        {/* Left side - Empty for balance */}
                        <div className="w-10"></div>

                        {/* Drag Handle - Centered and larger click area */}
                        <div
                            className="flex items-center justify-center py-3 px-8 cursor-grab active:cursor-grabbing -my-2"
                            onMouseDown={handleDragStart}
                            onTouchStart={handleDragStart}
                            onTouchMove={handleDragMove}
                            onTouchEnd={handleDragEnd}
                            style={{
                                touchAction: 'none',
                                WebkitUserSelect: 'none',
                                userSelect: 'none'
                            }}
                        >
                            <div className="w-12 h-1.5 bg-gray-400 rounded-full" />
                        </div>

                        {/* Right side - Only Share Button */}
                        <div className="flex items-center space-x-2">
                            {/* Share Button */}
                            <button
                                onClick={handleShareContact}
                                className="p-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 border border-green-500 shadow-lg hover:shadow-xl"
                                title="Share Contact"
                            >
                                <Share2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Scrollable Content - Enhanced styling */}
                <div
                    ref={contentRef}
                    className="flex-1 overflow-y-auto overflow-x-hidden px-6 pb-6 pt-4 bg-gray-900"
                    style={{
                        scrollBehavior: 'smooth',
                        WebkitOverflowScrolling: 'touch'
                    }}
                >
                    <div className="space-y-8">
                        {/* Profile Header - Enhanced luxury feel with better spacing for long names */}
                        <div className="py-8 text-center border-b border-gray-700">
                            {/* Large Centered Profile Picture with Status Overlay */}
                            <button
                                onClick={() => setIsImageExpanded(true)}
                                className="w-28 h-28 bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl flex items-center justify-center overflow-hidden shadow-2xl mx-auto mb-6 hover:shadow-3xl transition-all duration-300 cursor-pointer border-2 border-gray-600 hover:border-gray-500 relative"
                            >
                                {formData.profile_image_url ? (
                                    <img
                                        src={formData.profile_image_url}
                                        alt="Contact"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-gray-200 font-bold text-3xl tracking-wider">
                                        {formData.first_name?.[0]}{formData.last_name?.[0]}
                                    </span>
                                )}

                                {/* Green overlay for contacted status - REMOVED for basic contacts view */}
                            </button>

                            {/* Name and Company - Enhanced typography with better wrapping */}
                            <div className="max-w-lg mx-auto px-4">
                                {isEditing ? (
                                    <div className="space-y-3">
                                        {/* Single input for full name */}
                                        <input
                                            type="text"
                                            value={`${formData.first_name || ''} ${formData.last_name || ''}`.trim()}
                                            onChange={(e) => {
                                                const names = e.target.value.trim().split(' ');
                                                const firstName = names[0] || '';
                                                const lastName = names.slice(1).join(' ') || '';
                                                handleInputChangeWithTracking('first_name', firstName);
                                                handleInputChangeWithTracking('last_name', lastName);
                                            }}
                                            placeholder="Full Name"
                                            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-center text-xl font-bold placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                        {/* Single combined input for title and company */}
                                        <input
                                            type="text"
                                            value={`${formData.title || ''}${formData.title && formData.company ? ' at ' : ''}${formData.company || ''}`.trim()}
                                            onChange={(e) => {
                                                const value = e.target.value.trim();
                                                if (value.includes(' at ')) {
                                                    const [title, company] = value.split(' at ');
                                                    handleInputChangeWithTracking('title', title.trim());
                                                    handleInputChangeWithTracking('company', company.trim());
                                                } else {
                                                    // If no ' at ' separator, treat the whole thing as title
                                                    handleInputChangeWithTracking('title', value);
                                                    handleInputChangeWithTracking('company', '');
                                                }
                                            }}
                                            placeholder="Title at Company"
                                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-gray-300 text-center text-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight break-words mb-4 tracking-tight px-2">
                                            {formData.first_name} {formData.last_name}
                                        </h2>
                                        {(formData.title || formData.company) && (
                                            <p className="text-gray-300 text-base md:text-lg leading-relaxed break-words font-medium px-2">
                                                {formData.title} {formData.title && formData.company && 'at'} {formData.company}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Smart Note Input - Below profile picture */}
                            <div className="mt-6">
                                {!showSmartNoteInput ? (
                                    <div className="flex justify-center space-x-3">
                                        <button
                                            onClick={() => setShowSmartNoteInput(true)}
                                            className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white hover:bg-gray-800 transition-colors border border-gray-700 hover:border-gray-600"
                                        >
                                            <Plus className="w-4 h-4" />
                                            <span className="text-sm font-medium">Add Smart Note</span>
                                        </button>

                                        {noteHistory.length > 0 && (
                                            <button
                                                onClick={() => setShowNoteHistory(true)}
                                                className="flex items-center space-x-2 px-4 py-2 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors border border-gray-700 hover:border-gray-600"
                                            >
                                                <BookOpen className="w-4 h-4" />
                                                <span className="text-sm">View Note History ({noteHistory.length})</span>
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-700/50 rounded-xl p-4">
                                        <h4 className="text-white font-medium mb-3 flex items-center">
                                            <Plus className="w-4 h-4 mr-2 text-purple-400" />
                                            Smart Note Input
                                        </h4>
                                        <p className="text-gray-300 text-sm mb-3">
                                            Paste or type your notes about {formData.first_name} and AI will automatically organize them into the appropriate fields.
                                        </p>

                                        <textarea
                                            value={smartNoteContent}
                                            onChange={(e) => setSmartNoteContent(e.target.value)}
                                            placeholder="Type or paste your notes here... AI will automatically parse and organize the information into the correct fields."
                                            className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                                            rows={6}
                                        />

                                        <div className="flex justify-between items-center mt-3">
                                            <button
                                                onClick={() => {
                                                    setShowSmartNoteInput(false);
                                                    setSmartNoteContent('');
                                                }}
                                                className="px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
                                            >
                                                Cancel
                                            </button>

                                            <button
                                                onClick={handleSmartNoteParsing}
                                                disabled={!smartNoteContent.trim() || isParsingNote}
                                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 border border-purple-500 shadow-lg hover:shadow-xl flex items-center text-sm font-medium"
                                            >
                                                {isParsingNote ? (
                                                    <>
                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                                        Parsing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Bot className="w-4 h-4 mr-2" />
                                                        Parse & Save
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Tab Navigation */}
                        <div className="border-b border-gray-700">
                            <nav className="flex space-x-8">
                                <button
                                    onClick={() => setActiveTab('details')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'details'
                                        ? 'border-blue-500 text-blue-400'
                                        : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                                        }`}
                                >
                                    Details
                                </button>
                                <button
                                    onClick={() => setActiveTab('options')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors flex items-center ${activeTab === 'options'
                                        ? 'border-orange-500 text-orange-400'
                                        : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                                        }`}
                                >
                                    <Settings className="w-4 h-4 mr-1" />
                                    Options
                                </button>
                                <button
                                    onClick={() => setActiveTab('ai')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors flex items-center ${activeTab === 'ai'
                                        ? 'border-purple-500 text-purple-400'
                                        : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                                        }`}
                                >
                                    <Bot className="w-4 h-4 mr-1" />
                                    AI
                                </button>
                            </nav>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'details' && (
                            <>
                                {/* Contact Information */}
                                <ContactInfo
                                    formData={formData}
                                    isEditing={isEditing}
                                    onInputChange={handleInputChangeWithTracking}
                                />

                                {/* Personal Details Section */}
                                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6">
                                    <h3 className="font-light text-white mb-6 flex items-center text-lg tracking-wide">
                                        <Users className="w-5 h-5 mr-3 text-purple-400" />
                                        PERSONAL DETAILS
                                    </h3>
                                    {isEditing ? (
                                        <textarea
                                            value={formData.personal_details || ''}
                                            onChange={(e) => handleInputChangeWithTracking('personal_details', e.target.value)}
                                            placeholder="Add personal details about when and where you met this contact, their family, interests, or any memorable interactions..."
                                            className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 resize-none"
                                            rows={4}
                                            style={{ minHeight: '100px' }}
                                        />
                                    ) : (
                                        <div className="text-gray-200 whitespace-pre-wrap bg-gray-900 p-4 text-sm border border-gray-700">
                                            {formData.personal_details || 'No personal details yet. Use Smart Note to add personal information about your relationship with this contact.'}
                                        </div>
                                    )}
                                </div>

                                {/* Investment Scores Section */}
                                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6">
                                    <h3 className="font-light text-white mb-6 flex items-center text-lg tracking-wide">
                                        <TrendingUp className="w-5 h-5 mr-3 text-green-400" />
                                        INVESTMENT INTELLIGENCE
                                    </h3>

                                    <div className="grid grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Personal Investment Score (1-10)
                                            </label>
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="10"
                                                    value={formData.personal_investment_score || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('personal_investment_score', e.target.value ? parseInt(e.target.value) : null)}
                                                    className="w-full p-3 bg-gray-900 border border-gray-700 text-gray-200 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                                                />
                                            ) : (
                                                <div className="text-2xl font-bold text-green-400">
                                                    {formData.personal_investment_score ? `${formData.personal_investment_score}/10` : 'Not scored'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Referral Potential Score (1-10)
                                            </label>
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="10"
                                                    value={formData.referral_potential_score || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('referral_potential_score', e.target.value ? parseInt(e.target.value) : null)}
                                                    className="w-full p-3 bg-gray-900 border border-gray-700 text-gray-200 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                                                />
                                            ) : (
                                                <div className="text-2xl font-bold text-blue-400">
                                                    {formData.referral_potential_score ? `${formData.referral_potential_score}/10` : 'Not scored'}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Investment Reasoning
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.personal_investment_reasoning || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('personal_investment_reasoning', e.target.value)}
                                                    placeholder="Why this investment score? Include financial capacity, risk tolerance, investment experience..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 resize-none"
                                                    rows={3}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.personal_investment_reasoning || 'No investment reasoning provided'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Referral Potential Reasoning
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.referral_potential_reasoning || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('referral_potential_reasoning', e.target.value)}
                                                    placeholder="Why this referral score? Network size, influence, willingness to refer..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 resize-none"
                                                    rows={3}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.referral_potential_reasoning || 'No referral reasoning provided'}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Financial Profile Section */}
                                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6">
                                    <h3 className="font-light text-white mb-6 flex items-center text-lg tracking-wide">
                                        <Users className="w-5 h-5 mr-3 text-yellow-400" />
                                        FINANCIAL PROFILE
                                    </h3>

                                    <div className="grid grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Estimated Investment Capacity
                                            </label>
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    value={formData.estimated_investment_capacity || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('estimated_investment_capacity', e.target.value)}
                                                    placeholder="e.g., $10K-$50K, $100K+, $1M+"
                                                    className="w-full p-3 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-3 border border-gray-700">
                                                    {formData.estimated_investment_capacity || 'Not assessed'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Accredited Investor Likelihood
                                            </label>
                                            {isEditing ? (
                                                <select
                                                    value={formData.accredited_investor_likelihood || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('accredited_investor_likelihood', e.target.value)}
                                                    className="w-full p-3 bg-gray-900 border border-gray-700 text-gray-200 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                                                >
                                                    <option value="">Select likelihood</option>
                                                    <option value="High">High</option>
                                                    <option value="Medium">Medium</option>
                                                    <option value="Low">Low</option>
                                                    <option value="Unknown">Unknown</option>
                                                </select>
                                            ) : (
                                                <div className={`text-gray-200 bg-gray-900 p-3 border border-gray-700 ${formData.accredited_investor_likelihood === 'High' ? 'text-green-400' :
                                                    formData.accredited_investor_likelihood === 'Medium' ? 'text-yellow-400' :
                                                        formData.accredited_investor_likelihood === 'Low' ? 'text-red-400' : ''
                                                    }`}>
                                                    {formData.accredited_investor_likelihood || 'Not assessed'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Financial Sophistication
                                            </label>
                                            {isEditing ? (
                                                <select
                                                    value={formData.financial_sophistication || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('financial_sophistication', e.target.value)}
                                                    className="w-full p-3 bg-gray-900 border border-gray-700 text-gray-200 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                                                >
                                                    <option value="">Select level</option>
                                                    <option value="Very High">Very High</option>
                                                    <option value="High">High</option>
                                                    <option value="Medium">Medium</option>
                                                    <option value="Low">Low</option>
                                                    <option value="Very Low">Very Low</option>
                                                </select>
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-3 border border-gray-700">
                                                    {formData.financial_sophistication || 'Not assessed'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Analysis Date
                                            </label>
                                            <div className="text-gray-400 bg-gray-900 p-3 border border-gray-700 text-sm">
                                                {formData.analysis_date ? new Date(formData.analysis_date).toLocaleDateString() : 'Not analyzed'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Strategy & Approach Section */}
                                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6">
                                    <h3 className="font-light text-white mb-6 flex items-center text-lg tracking-wide">
                                        <BookOpen className="w-5 h-5 mr-3 text-purple-400" />
                                        STRATEGY & INSIGHTS
                                    </h3>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Recommended Approach
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.recommended_approach || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('recommended_approach', e.target.value)}
                                                    placeholder="How should you approach this contact? Investment style, communication preferences..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 resize-none"
                                                    rows={3}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.recommended_approach || 'No approach strategy defined'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Key Factors
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.key_factors || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('key_factors', e.target.value)}
                                                    placeholder="Important factors to consider: industry connections, past investments, interests..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 resize-none"
                                                    rows={3}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.key_factors || 'No key factors identified'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Investment Summary
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.summary || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('summary', e.target.value)}
                                                    placeholder="Overall investment profile summary..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 resize-none"
                                                    rows={4}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.summary || 'No investment summary provided'}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Status & Tier Management */}
                                <StatusTierManager
                                    formData={formData}
                                    isEditing={isEditing}
                                    onInputChange={handleInputChangeWithTracking}
                                />

                                {/* Professional Background Section */}
                                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6">
                                    <h3 className="font-light text-white mb-6 flex items-center text-lg tracking-wide">
                                        <BookOpen className="w-5 h-5 mr-3 text-blue-400" />
                                        PROFESSIONAL BACKGROUND
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                LinkedIn Description
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.linkedin_description || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('linkedin_description', e.target.value)}
                                                    placeholder="Professional description from LinkedIn profile..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                                    rows={3}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.linkedin_description || 'No LinkedIn description available'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Professional Experience
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.professional_experience || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('professional_experience', e.target.value)}
                                                    placeholder="Work history, career progression, notable roles..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                                    rows={4}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.professional_experience || 'No professional experience recorded'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Education Background
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.education_background || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('education_background', e.target.value)}
                                                    placeholder="Educational history, degrees, certifications..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                                    rows={3}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.education_background || 'No education background recorded'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Skills & Expertise
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.skills_expertise || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('skills_expertise', e.target.value)}
                                                    placeholder="Technical skills, areas of expertise, specializations..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                                    rows={3}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.skills_expertise || 'No skills or expertise recorded'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Achievements
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.achievements || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('achievements', e.target.value)}
                                                    placeholder="Notable achievements, awards, recognitions..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                                    rows={3}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.achievements || 'No achievements recorded'}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Personal Information Section */}
                                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6">
                                    <h3 className="font-light text-white mb-6 flex items-center text-lg tracking-wide">
                                        <Users className="w-5 h-5 mr-3 text-green-400" />
                                        PERSONAL INFORMATION
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Personal Interests
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.personal_interests || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('personal_interests', e.target.value)}
                                                    placeholder="Hobbies, interests, passions, activities..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 resize-none"
                                                    rows={3}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.personal_interests || 'No personal interests recorded'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Location Information
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.location_info || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('location_info', e.target.value)}
                                                    placeholder="Current location, previous locations, travel preferences..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 resize-none"
                                                    rows={2}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.location_info || 'No location information recorded'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Company Details
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.company_details || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('company_details', e.target.value)}
                                                    placeholder="Company size, culture, recent news, industry position..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 resize-none"
                                                    rows={3}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.company_details || 'No company details recorded'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Contact Preferences
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.contact_preferences || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('contact_preferences', e.target.value)}
                                                    placeholder="Preferred communication methods, best times to contact, communication style..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 resize-none"
                                                    rows={2}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.contact_preferences || 'No contact preferences recorded'}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Comprehensive Notes Section */}
                                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6">
                                    <h3 className="font-light text-white mb-6 flex items-center text-lg tracking-wide">
                                        <BookOpen className="w-5 h-5 mr-3 text-yellow-400" />
                                        COMPREHENSIVE NOTES
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                TLDR Summary
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.tldr_summary || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('tldr_summary', e.target.value)}
                                                    placeholder="Brief summary of key points about this contact..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500 resize-none"
                                                    rows={2}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.tldr_summary || 'No TLDR summary available'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                About Section
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.about_section || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('about_section', e.target.value)}
                                                    placeholder="Detailed about section, background information..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500 resize-none"
                                                    rows={4}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.about_section || 'No about section recorded'}
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Comprehensive Notes
                                            </label>
                                            {isEditing ? (
                                                <textarea
                                                    value={formData.comprehensive_notes || ''}
                                                    onChange={(e) => handleInputChangeWithTracking('comprehensive_notes', e.target.value)}
                                                    placeholder="Detailed notes, observations, meeting notes..."
                                                    className="w-full p-4 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500 resize-none"
                                                    rows={5}
                                                />
                                            ) : (
                                                <div className="text-gray-200 bg-gray-900 p-4 border border-gray-700 whitespace-pre-wrap">
                                                    {formData.comprehensive_notes || 'No comprehensive notes recorded'}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'options' && (
                            <div className="space-y-6">
                                {/* Options Section */}
                                <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 p-6">
                                    <h3 className="font-light text-white mb-6 flex items-center text-lg tracking-wide">
                                        <Settings className="w-5 h-5 mr-3 text-orange-400" />
                                        CONTACT OPTIONS
                                    </h3>

                                    <div className="space-y-4">
                                        {/* Add Audio / Transcribe */}
                                        <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700 rounded-lg">
                                            <div className="flex items-center">
                                                <UploadCloud className="w-5 h-5 mr-3 text-blue-400" />
                                                <div>
                                                    <h4 className="text-white font-medium">Add Audio / Transcribe</h4>
                                                    <p className="text-gray-400 text-sm">Upload an audio file and save the transcript as an event for this contact</p>
                                                    {transError && <p className="text-xs text-red-400 mt-1">{transError}</p>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="file"
                                                    accept="audio/*"
                                                    className="hidden"
                                                    id="contact-audio-upload"
                                                    onChange={(e) => setAudioFile((e.target.files || [])[0] || null)}
                                                />
                                                <label htmlFor="contact-audio-upload" className="px-3 py-2 bg-gray-800 text-gray-200 rounded-md border border-gray-700 hover:bg-gray-700 cursor-pointer">
                                                    {audioFile ? 'Change File' : 'Choose File'}
                                                </label>
                                                <button
                                                    onClick={handleStartTranscribe}
                                                    disabled={!audioFile || transBusy}
                                                    className="px-3 py-2 bg-blue-600 text-white rounded-md border border-blue-500 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                >
                                                    {transBusy ? (
                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    ) : (
                                                        <Mic className="w-4 h-4" />
                                                    )}
                                                    {transBusy ? 'Processing…' : 'Transcribe'}
                                                </button>
                                            </div>
                                        </div>
                                        {/* Edit Button */}
                                        <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors">
                                            <div className="flex items-center">
                                                <Edit3 className="w-5 h-5 mr-3 text-blue-400" />
                                                <div>
                                                    <h4 className="text-white font-medium">Edit Contact</h4>
                                                    <p className="text-gray-400 text-sm">Modify contact information and details</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={toggleEditMode}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 border border-blue-500 shadow-lg hover:shadow-xl"
                                            >
                                                {isEditing ? 'Cancel' : 'Edit'}
                                            </button>
                                        </div>

                                        {/* Archive Button */}
                                        <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors">
                                            <div className="flex items-center">
                                                {contact.is_archived ? (
                                                    <ArchiveRestore className="w-5 h-5 mr-3 text-green-400" />
                                                ) : (
                                                    <Archive className="w-5 h-5 mr-3 text-yellow-400" />
                                                )}
                                                <div>
                                                    <h4 className="text-white font-medium">
                                                        {contact.is_archived ? 'Unarchive Contact' : 'Archive Contact'}
                                                    </h4>
                                                    <p className="text-gray-400 text-sm">
                                                        {contact.is_archived
                                                            ? 'Move contact back to active contacts'
                                                            : 'Remove contact from active view'
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={handleArchiveToggle}
                                                disabled={isArchiving}
                                                className={`px-4 py-2 text-white rounded-lg transition-all duration-200 border shadow-lg hover:shadow-xl disabled:opacity-50 ${contact.is_archived
                                                    ? 'bg-green-600 hover:bg-green-700 border-green-500'
                                                    : 'bg-yellow-600 hover:bg-yellow-700 border-yellow-500'
                                                    }`}
                                            >
                                                {isArchiving ? (
                                                    <SquareLoader size="xs" />
                                                ) : contact.is_archived ? (
                                                    'Unarchive'
                                                ) : (
                                                    'Archive'
                                                )}
                                            </button>
                                        </div>

                                        {/* Collection Status Toggle - Only show when in a collection */}
                                        {collectionContext && (
                                            <div className="flex items-center justify-between p-4 bg-gray-900 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors">
                                                <div className="flex items-center">
                                                    <Check className="w-5 h-5 mr-3 text-green-400" />
                                                    <div>
                                                        <h4 className="text-white font-medium">Contact Status</h4>
                                                        <p className="text-gray-400 text-sm">
                                                            Mark as {formData.status === 'Called' ? 'not contacted' : 'contacted'} in this collection
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={handleToggleContactStatus}
                                                    className={`px-4 py-2 text-white rounded-lg transition-all duration-200 border shadow-lg hover:shadow-xl ${formData.status === 'Called' || formData.status === 'Follow-up' || formData.status === 'Meeting Scheduled' || formData.status === 'Closed'
                                                        ? 'bg-green-600 hover:bg-green-700 border-green-500'
                                                        : 'bg-gray-600 hover:bg-gray-700 border-gray-500'
                                                        }`}
                                                >
                                                    {formData.status === 'Called' || formData.status === 'Follow-up' || formData.status === 'Meeting Scheduled' || formData.status === 'Closed'
                                                        ? 'Contacted'
                                                        : 'Not Contacted'
                                                    }
                                                </button>
                                            </div>
                                        )}

                                        {/* Edit Mode Actions */}
                                        {isEditing && hasChanges && (
                                            <div className="flex items-center justify-between p-4 bg-red-900/30 border border-red-700 rounded-lg">
                                                <div className="flex items-center">
                                                    <X className="w-5 h-5 mr-3 text-red-400" />
                                                    <div>
                                                        <h4 className="text-white font-medium">Unsaved Changes</h4>
                                                        <p className="text-gray-400 text-sm">You have unsaved changes to this contact</p>
                                                    </div>
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={handleDiscard}
                                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 border border-red-500"
                                                    >
                                                        Discard
                                                    </button>
                                                    <button
                                                        onClick={onSaveWithCallback}
                                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 border border-green-500"
                                                    >
                                                        Save
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'ai' && user && (
                            <div className="space-y-6">
                                {/* Natural Search Section */}
                                <div className="bg-black border border-gray-800 p-6">
                                    {/* Header */}
                                    <div className="flex items-center space-x-3 mb-4">
                                        <div className="w-8 h-8 bg-gray-900 border border-gray-700 flex items-center justify-center">
                                            <Search className="w-4 h-4 text-white" />
                                        </div>
                                        <h3 className="text-lg font-light text-white tracking-wide">ASK ABOUT THIS CONTACT</h3>
                                    </div>

                                    <div className="space-y-4">
                                        {/* Search Input */}
                                        <div className="flex space-x-3">
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                placeholder="Ask anything about this contact..."
                                                className="flex-1 p-3 bg-gray-900 border border-gray-700 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-gray-600 text-sm"
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter' && !isSearching) {
                                                        handleNaturalSearch(false);
                                                    }
                                                }}
                                            />
                                            <button
                                                onClick={() => handleNaturalSearch(false)}
                                                disabled={!searchQuery.trim() || isSearching}
                                                className="px-4 py-3 bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-gray-700 hover:border-gray-600 flex items-center justify-center min-w-[100px]"
                                            >
                                                {isSearching ? (
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                ) : (
                                                    <>
                                                        <Search className="w-4 h-4 mr-2" />
                                                        Search
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {/* Search Result */}
                                        {searchResult && (
                                            <div className="bg-gray-900 border border-gray-700 p-4">
                                                <h4 className={`font-medium mb-3 flex items-center text-sm ${isGuessingMode ? 'text-orange-400' : 'text-blue-400'}`}>
                                                    <Bot className="w-4 h-4 mr-2" />
                                                    {isGuessingMode ? 'AI Educated Guess' : 'AI Answer'}
                                                </h4>
                                                <div className="text-gray-200 whitespace-pre-wrap text-sm leading-relaxed">
                                                    {searchResult}
                                                </div>

                                                {/* Follow-up Yes/No buttons for educated guess */}
                                                {showGuessOption && (
                                                    <div className="mt-4 pt-4 border-t border-gray-700">
                                                        <p className="text-gray-300 text-sm mb-3">Would you like me to make an educated guess?</p>
                                                        <div className="flex space-x-3">
                                                            <button
                                                                onClick={handleEducatedGuess}
                                                                className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 transition-colors border border-gray-700 hover:border-gray-600 text-sm font-medium"
                                                            >
                                                                Yes, make a guess
                                                            </button>
                                                            <button
                                                                onClick={handleDeclineGuess}
                                                                className="px-4 py-2 bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors border border-gray-700 hover:border-gray-600 text-sm font-medium"
                                                            >
                                                                No, thanks
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                    </div>
                                </div>

                                <PersonalizedMessageGenerator
                                    contact={formData}
                                    user={user}
                                    personalProfile={personalProfile}
                                    collectionContext={collectionContext}
                                    collectionName={collectionName}
                                    collectionId={collectionId}
                                    onNotification={showNotification}
                                    onSave={onSave}
                                />
                                <TLDRGenerator
                                    contact={formData}
                                    user={user}
                                    personalProfile={personalProfile}
                                    onNotification={showNotification}
                                    onSave={onSave}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Save Button Footer (when editing and has changes) - Enhanced styling */}
                {isEditing && hasChanges && (
                    <div className="flex-shrink-0 bg-gradient-to-t from-gray-800 to-gray-750 border-t border-gray-600 px-6 py-4 shadow-lg">
                        <button
                            onClick={onSaveWithCallback}
                            disabled={loading}
                            className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-bold text-lg border border-blue-500 shadow-xl hover:shadow-2xl"
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                )}
            </div>

            {/* Expanded Image Modal */}
            {isImageExpanded && formData.profile_image_url && (
                <ExpandedImageModal
                    imageUrl={formData.profile_image_url}
                    onClose={() => setIsImageExpanded(false)}
                />
            )}

            {/* Transcribe Save Event Modal */}
            {showTranscribeModal && (
                <TranscribeSaveEventModal
                    open={showTranscribeModal}
                    onClose={() => setShowTranscribeModal(false)}
                    onConfirm={async (payload) => {
                        try {
                            const ownerId = String(contact.id);
                            await createEvent(ownerId, payload as any);
                            setShowTranscribeModal(false);
                            setAudioFile(null);
                            setTransSegments([]);
                            setTransText('');
                            showNotification('Transcript saved as an event', 'success');
                            if (onSave) onSave();
                        } catch (e: any) {
                            showNotification(e?.message || 'Failed to save event', 'error');
                        }
                    }}
                    defaultOwnerContactId={String(contact.id)}
                    participants={[{ contactId: String(contact.id), name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Contact', avatar: contact.profile_image_url || null }]}
                    segments={transSegments}
                    fullText={transText}
                    speakerMap={(transSegments || []).reduce((acc, s) => { acc[s.speaker || 'Speaker'] = String(contact.id); return acc; }, {} as Record<string, string | null>)}
                />
            )}
            {/* Share Modal */}
            {showShareModal && (
                <ShareModal
                    formData={formData}
                    onClose={() => setShowShareModal(false)}
                    onNotification={showNotification}
                />
            )}

            {/* Notification Toast - Disabled */}
            {/* {notification && (
                    <NotificationToast
                        notification={notification}
                        onClose={hideNotification}
                    />
                )} */}            {/* Note History Modal */}
            {showNoteHistory && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[10002] flex items-center justify-center p-4">
                    <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl shadow-2xl border border-gray-600 w-full max-w-2xl max-h-[80vh] overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-700">
                            <h3 className="text-xl font-bold text-white flex items-center">
                                <BookOpen className="w-5 h-5 mr-2 text-purple-400" />
                                Note History
                            </h3>
                            <button
                                onClick={() => setShowNoteHistory(false)}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            {noteHistory.length === 0 ? (
                                <div className="text-center text-gray-400 py-8">
                                    <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>No note history available</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {noteHistory.map((note) => (
                                        <div key={note.id} className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-gray-400">
                                                    {new Date(note.date).toLocaleString()}
                                                </span>
                                            </div>
                                            <div className="text-gray-200 whitespace-pre-wrap text-sm">
                                                {note.content}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

